import { queryOptions, skipToken, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { Layout } from '../layout/entity';
import { templateKeys, type Template } from './entity';
import { parseTemplateWithLayouts, populateTemplateWithLayouts } from './populate';

const STALE_TIME = 5 * 60 * 1000;

/** @public */
export async function getTemplate(
  workspaceId: number,
  templateId: number,
  qc: QueryClient,
  signal?: AbortSignal,
): Promise<Template> {
  const raw = await hydraUiApi.templates.get({
    params: { path: { environment_id: String(workspaceId), template_id: String(templateId) } },
    signal,
  });
  if (raw.template == null) {
    throw new Error(`template ${templateId} not found`);
  }
  const parsed = parseTemplateWithLayouts(raw.template, raw.layouts);
  populateTemplateWithLayouts(qc, workspaceId, parsed);
  return parsed.template;
}

/** @public */
export const getTemplateQueryOptions = (workspaceId: number, templateId: number, qc: QueryClient) =>
  queryOptions({
    queryKey: templateKeys.detail(workspaceId, String(templateId)),
    queryFn: ({ signal }) => getTemplate(workspaceId, templateId, qc, signal),
    staleTime: STALE_TIME,
  });

/**
 * The layouts the template's last read side-loaded. Only `getTemplate` fills
 * this slot, so the query never fetches: it reads whatever the template query
 * last wrote, and stays empty until then.
 *
 * @public
 */
export const getTemplateLayoutsQueryOptions = (workspaceId: number, templateId: number) =>
  queryOptions<readonly Layout[]>({
    queryKey: templateKeys.layouts(workspaceId, String(templateId)),
    queryFn: skipToken,
  });

interface UseTemplateOptionalOptions {
  workspaceId: number;
  templateId: number;
  enabled?: boolean;
  /** How long a read stays fresh. Pass `0` to read the template again on every mount. */
  staleTime?: number;
}

/**
 * Non-suspense, conditionally-enabled by-id hook (see `useNewsletterOptional`).
 *
 * @public
 */
export const useTemplateOptional = ({
  workspaceId,
  templateId,
  enabled = true,
  staleTime = STALE_TIME,
}: UseTemplateOptionalOptions) => {
  const qc = useQueryClient();
  const active = enabled && workspaceId > 0 && templateId > 0;
  return useQuery({
    queryKey: active ? templateKeys.detail(workspaceId, String(templateId)) : ['template', 'inactive'],
    queryFn: active ? ({ signal }) => getTemplate(workspaceId, templateId, qc, signal) : skipToken,
    staleTime,
    retry: false,
  });
};

export async function getTemplateVariables(workspaceId: number, templateId: string, signal?: AbortSignal) {
  return hydraUiApi.templates.getVariables({
    params: { path: { environment_id: String(workspaceId), template_id: templateId } },
    signal,
  });
}

export const getTemplateVariablesQueryOptions = (workspaceId: number, templateId: string) =>
  queryOptions({
    queryKey: [...templateKeys.detail(workspaceId, templateId), 'variables'],
    queryFn: ({ signal }) => getTemplateVariables(workspaceId, templateId, signal),
    staleTime: Infinity,
    retry: false,
  });
